import { motion, AnimatePresence } from "framer-motion";
import { 
  Mail, 
  MessageSquare, 
  Phone, 
  Bell, 
  Globe, 
  Smartphone, 
  Monitor,
  Users,
  CheckCircle2,
  Zap,
  TrendingUp,
  BarChart3
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_multi-channel_communication_network_background.png";

export default function CommunicationHubCard() {
  const features = [
    {
      icon: Mail,
      title: "Intelligent Email Automation",
      desc: "Personalized appointment confirmations, care instructions, and billing statements with AI-optimized send times"
    },
    {
      icon: MessageSquare,
      title: "SMS / Text Messaging",
      desc: "Two-way texting for reminders, check-in links, prescription alerts, and real-time patient communication"
    },
    {
      icon: Phone,
      title: "Voice / Phone Integration",
      desc: "Automated voice calls for reminders, IVR self-service, and AI-powered call routing"
    },
    {
      icon: Bell,
      title: "Push Notifications",
      desc: "Instant mobile alerts for wait time updates, lab results, care reminders, and urgent communications"
    },
    {
      icon: Smartphone,
      title: "Mobile App Engagement",
      desc: "Native in-app messaging, notifications, and chat for on-the-go access to care team"
    }
  ];

  const kpis = [
    { label: "Patient Reachability", value: "99%", icon: Users, color: "text-emerald-400" },
    { label: "Message Delivery Rate", value: "98%", icon: CheckCircle2, color: "text-blue-400" },
    { label: "Response Time", value: "< 2 min", icon: Zap, color: "text-pink-400" },
    { label: "No-Show Reduction", value: "-45%", icon: TrendingUp, color: "text-amber-400" },
    { label: "Patient Engagement", value: "+60%", icon: BarChart3, color: "text-purple-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Omnichannel
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            Multi-Channel Communication Hub
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            Every Channel. One Unified Voice.
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Reach patients, providers, and employers wherever they are — seamlessly, instantly, intelligently."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>


          {/* Floating Channel Icons with Messages */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             
             {/* SMS Bubble - Top Left */}
             <motion.div
               animate={{ y: [0, -15, 0], x: [0, 5, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 }}
               className="absolute top-[8%] left-[4%] flex items-center gap-4"
             >
               <div className="bg-green-500 text-white p-4 rounded-full shadow-lg flex items-center justify-center w-14 h-14">
                 <MessageSquare className="w-7 h-7" />
               </div>
               <div className="bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-slate-700 max-w-[140px]">
                 "Your appointment is confirmed!"
               </div>
             </motion.div>

             {/* Email Bubble - Middle Left */}
             <motion.div
               animate={{ y: [0, 15, 0], x: [0, -5, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               className="absolute top-[38%] left-[8%] flex items-center gap-4"
             >
               <div className="bg-blue-500 text-white p-4 rounded-full shadow-lg flex items-center justify-center w-14 h-14">
                 <Mail className="w-7 h-7" />
               </div>
               <div className="bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-slate-700 max-w-[140px]">
                 "View your latest lab results"
               </div>
             </motion.div>

             {/* Phone Bubble - Top Right */}
             <motion.div
               animate={{ y: [0, -10, 0], x: [0, -8, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
               className="absolute top-[12%] right-[4%] flex flex-row-reverse items-center gap-4"
             >
               <div className="bg-purple-500 text-white p-4 rounded-full shadow-lg flex items-center justify-center w-14 h-14">
                 <Phone className="w-7 h-7" />
               </div>
               <div className="bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-slate-700 max-w-[140px] text-right">
                 "Press 1 to reschedule"
               </div>
             </motion.div>

             {/* Notification Bubble - Middle Right */}
             <motion.div
               animate={{ y: [0, 12, 0], x: [0, 6, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
               className="absolute top-[42%] right-[8%] flex flex-row-reverse items-center gap-4"
             >
               <div className="bg-amber-500 text-white p-4 rounded-full shadow-lg flex items-center justify-center w-14 h-14">
                 <Bell className="w-7 h-7" />
               </div>
               <div className="bg-white/90 backdrop-blur px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-slate-700 max-w-[140px] text-right">
                 "Head over to Room 103"
               </div>
             </motion.div>

          </div>

          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

